/**
 * SCR-04 gameplay (Flow B/C). Every rule runs in @mp/core: taps become actions with
 * active-play time, `apply()` mutates the round immediately, and the returned events are
 * animated by the views (queued flights/pops, never dropped taps).
 */
import * as Phaser from 'phaser';
import {
  Op,
  Status,
  canExtraSlot,
  canMagnet,
  canShuffle,
  canUndo,
  compileLevel,
  createRound,
  isTappable,
  lineShown,
  setsRemaining,
  type CompiledLevel,
  type LevelV1,
  type Round,
  type RoundEvent,
  type RoundOutcome,
} from '@mp/core';
import { economy, itemDef, type BoosterKey, type Grant } from '../config/content.js';
import { app } from '../game/context.js';
import { loadGlyphs } from '../game/glyphs.js';
import { playLayout, piecePos, type PlayLayout } from '../game/layout.js';
import { C, FONT, ITEM_COLORS } from '../game/theme.js';
import { vp } from '../game/viewport.js';
import { atMost, deco, dur, wait } from '../fx/motion.js';
import { Fx } from '../fx/particles.js';
import { has, t } from '../i18n/index.js';
import { ActiveClock } from '../services/clock.js';
import { hasCityOutfit, outfitFor } from '../services/cityOutfit.js';
import { NeedsUpdateError, needsAppUpdate } from '../services/levels.js';
import { MARK, hasMark, routeAfterWin, showHandPointer } from '../services/firstSession.js';
import { adFailureToast } from '../services/ads.js';
import { randomSeed } from '../services/ids.js';
import { applyDoubleTips, buyBooster, consumeBooster, payRescue, rescueOffer, type RescueMethod } from '../services/rewards.js';
import { RoundSession, buildSubmission, finishRound, newRecord, type RoundRecord } from '../services/rounds.js';
import { WIRE_MODE, assistFor, boosterBlock, boosterKeys, rulesOf, type CustomResult, type CustomRound, type CustomRules } from '../services/customRound.js';
import { op } from '../services/ops.js';
import { streakMilestoneDue } from '../services/prompts.js';
import { ERA_ORDER, cardSeen, cityShape, cityTitle, isGrandOpening, markCard, nextCity, orderOf, remindKey, remindersDue, replayTarget, shopFinale, shownCards } from '../services/cityRoles.js';
import { activeShop, isShopUnlocked } from '../services/progress.js';
import { clearerItemsOn } from '../services/state.js';
import { shopTitle } from '../ui/hud.js';
import { openView } from '../features/registry.js';
import { BagView } from '../views/bag.js';
import { bagShape } from '../views/bagSlots.js';
import { BoardView, TileView } from '../views/board.js';
import { BoosterBar } from '../views/boosters.js';
import { ConveyorView } from '../views/conveyor.js';
import { isConveyorPiece } from '../views/dispensers.js';
import { clearGoalHud, collectGoalHud, isGoldenPiece, orderDoneOf, rescueLeft, rescueSets } from '../views/goal.js';
import { faceDownLines, magnetHeldByHiddenLine, servedFaceDown } from '../views/hidden.js';
import { keyLines, magnetHeldByLocks, tapRefusal } from '../views/keys.js';
import { NO_LOOKS, itemLooks, type ItemLooks } from '../views/looks.js';
import { KEY_BADGE, bakeLockArt } from '../views/lock.js';
import { OrderView } from '../views/order.js';
import { flightDelays, returnedTiles, tappablePieces } from '../views/pairs.js';
import { PipView } from '../views/pip.js';
import { ShelfView } from '../views/shelf.js';
import { isShelfPiece, shelfState } from '../views/shelves.js';
import { trayPopOf } from '../views/trays.js';
import { ComboBadge, HandPointer, PreviewBubble, RushTimer, SlideBanner } from '../views/widgets.js';
import { recommendTap } from './bot.js';
import { cityName, journeyNow } from './MapScene.js';
import { fadeIn, go, onResizeSettled } from './nav.js';

const LONG_PRESS_MS = 380;

/** An item's name in the player's language (its catalog name when no translation has it). */
const itemName = (id: string): string => (has(`item.${id}`) ? t(`item.${id}`) : (itemDef(id)?.name ?? ''));

interface Resume {
  session: RoundSession;
  clock: ActiveClock;
}

export interface PlayData {
  /** campaign level number in the current city (a custom round's level.number otherwise) */
  level: number;
  resume?: Resume;
  /** a feature-launched round (daily, tournament, event, beat, duel): services/customRound.ts */
  custom?: CustomRound;
}

/** Where a city moment sends the player after the win: a level to replay, or the map (gliding to a city, or opening a view). */
type CityNext = { to: 'play'; city: string; level: number } | { to: 'map'; focus?: string; open?: string };

/** One piece in a Clearer items test hook: its engine item id and the art (atlas frame id) it is drawn with. */
interface LookRow {
  piece: number;
  item: string;
  art: string;
  real: boolean;
}

interface Press {
  piece: number;
  x: number;
  y: number;
  timer: Phaser.Time.TimerEvent;
  previewed: boolean;
}

interface PopJob {
  after: Array<(x: number, y: number) => void>;
}

const BOOSTER_OP: Record<BoosterKey, Op> = { undo: Op.Undo, shuffle: Op.Shuffle, magnet: Op.Magnet, extraSlot: Op.ExtraSlot };

export class PlayScene extends Phaser.Scene {
  levelNo = 1;
  L!: CompiledLevel;
  round!: Round;
  private rec!: RoundRecord;
  private clock!: ActiveClock;
  lay!: PlayLayout;
  board!: BoardView;
  private bag!: BagView;
  private order!: OrderView;
  private pip!: PipView;
  boosters: BoosterBar | null = null;
  conveyor: ConveyorView | null = null;
  /** Clearer items: the art each item of this round is drawn with (read once per build, looks.ts) */
  private looks: ItemLooks = NO_LOOKS;
  /** shelf (rules v2): the side shelf's row in the strip under the board */
  shelf: ShelfView | null = null;
  private timer: RushTimer | null = null;
  private combo!: ComboBadge;
  private hand!: HandPointer;
  private preview!: PreviewBubble;
  /** clearGoal: "Now clear the counter!"; collectGoal: "Now pop the golden items!" */
  private banner!: SlideBanner;
  private fx!: Fx;
  private hint: Phaser.GameObjects.Text | null = null;
  private locked = true;
  private ended = false;
  private session!: RoundSession;
  private press: Press | null = null;
  private tutorial = false;
  private tutorialTaps = 0;
  private lastTick = 0;
  private resume: Resume | null = null;
  private custom: CustomRound | null = null;
  private failReason: 'bagFull' | 'timeout' | 'stuck' | null = null;
  private rules: Required<CustomRules> = rulesOf(null);
  /** lockKey: taps on a padlocked tile refused so far (test hooks) */
  private lockRefusals = 0;
  /** shelf: taps on an item between the shelf's two ends refused so far (test hooks) */
  private shelfRefusals = 0;
  /** collectGoal: golden pieces popped in core whose sparkle has not reached the counter yet (it still counts them) */
  private goldenHeld = 0;
  ready = false;

  constructor() {
    super('play');
  }

  init(data: PlayData): void {
    this.levelNo = data.level;
    this.resume = data.resume ?? null;
    this.custom = data.custom ?? null;
    this.failReason = null;
    this.rules = rulesOf(this.custom);
    this.pendingFeature = null;
    this.locked = true;
    this.ended = false;
    this.ready = false;
    this.press = null;
    this.boosters = null;
    this.conveyor = null;
    this.shelf = null;
    this.timer = null;
    this.hint = null;
    this.tutorialTaps = 0;
    this.lockRefusals = 0;
    this.shelfRefusals = 0;
    this.goldenHeld = 0;
  }

  create(): void {
    const a = app();
    const label = this.custom?.label;
    a.ui.setHud({ mode: 'play', map: null, shop: null, play: { level: this.levelNo, ...(label ? { label } : {}), onPause: () => void this.pause() } });
    this.drawBackground();
    fadeIn(this);
    void this.start();
  }

  /** Static backdrop baked once into a texture (Graphics would re-tessellate every frame). */
  private drawBackground(): void {
    const { width: W, height: H } = this.scale;
    const key = `bg_play_${W}x${H}`;
    if (!this.textures.exists(key)) {
      const c = document.createElement('canvas');
      c.width = W;
      c.height = H;
      const ctx = c.getContext('2d')!;
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, '#fff0d6');
      grad.addColorStop(1, '#ffcf96');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);
      const u = vp.u;
      ctx.fillStyle = 'rgba(255,255,255,0.22)';
      for (let y = 0, row = 0; y < H; y += 46 * u, row++)
        for (let x = (row % 2) * 23 * u; x < W; x += 46 * u) {
          ctx.beginPath();
          ctx.arc(x, y, 5 * u, 0, Math.PI * 2);
          ctx.fill();
        }
      this.textures.addCanvas(key, c);
    }
    this.add.image(0, 0, key).setOrigin(0, 0).setDepth(0);
  }

  private async start(): Promise<void> {
    const a = app();
    const s = a.services;
    let r = this.resume;
    if (!r) {
      a.ui.setBusy(true);
      const custom = this.custom;
      let level: LevelV1;
      try {
        [level] = await Promise.all([
          custom ? Promise.resolve(custom.level) : s.levels.load(a.city, this.levelNo),
          // a feature's level may come from another city or an event: its items and customers
          custom ? a.art.loadArtGroups(a.art.levelGroups(custom.level)) : a.art.ensureGroup(`customers_${a.city}`),
        ]);
        if (needsAppUpdate(level.rulesVersion, level.mechanics)) throw new NeedsUpdateError(level.city, level.rulesVersion, level.mechanics); // a feature's level too
      } catch (e) {
        // roadmap-3k §8.1: a level that needs a newer engine is refused, back to the map
        if (!(e instanceof NeedsUpdateError)) throw e;
        a.ui.setBusy(false);
        a.ui.toast(t('map.updateToVisit', { city: cityName(e.city) }));
        if (this.scene.isActive()) go(this, 'map');
        return;
      }
      a.ui.setBusy(false);
      if (!this.scene.isActive()) return;
      const L = compileLevel(level);
      const fails = s.store.state.failStreak[level.id] ?? 0;
      // clearGoal: a clear-goal level carries no assist swaps (relabelling cannot make a full clear easier)
      const assist = level.goal === 'clear' ? 0 : assistFor(this.rules, fails);
      // the round economy the server expects; buildSubmission reads it back into header.cfg
      const round = createRound(L, {
        seed: custom ? custom.seed : randomSeed(),
        assist,
        config: { ...economy.round, rescueThreshold: a.remote.rescueThreshold },
      });
      if (assist) s.analytics.track('assist_applied', { level: this.levelNo, assist, fails });
      const clock = new ActiveClock(() => performance.now());
      r = { session: new RoundSession(round, newRecord(level.id, this.levelNo, s.now(), custom ? WIRE_MODE[custom.mode] : 'level'), clock), clock };
      s.store.update((st) => (st.roundBoosters = {})); // a new round: nothing of it is spent yet
    }
    this.session = r.session;
    this.round = r.session.round;
    this.L = this.round.L;
    this.rec = r.session.rec;
    this.clock = r.clock;
    // a canvas text keeps the face it was drawn with: a pack's item names (a Hanoi pack's) get the
    // Vietnamese face before a long press draws one into the preview bubble (it is ready long before)
    void loadGlyphs(this.L.items.map((id) => itemName(id)));
    this.build();
    this.wireInput();
    this.wireLifecycle();
    this.ready = true;
    // the level's city: its own play loop when its pack brought a theme (a rush level stays global)
    s.audio.music(this.L.rushMs > 0 ? 'rush' : 'play', this.L.level.city);
    if (this.resume) {
      this.locked = false;
      this.syncHud();
      return;
    }
    await this.intro();
    if (!this.scene.isActive()) return;
    if (this.custom?.beforeStart) {
      try {
        await this.custom.beforeStart();
      } catch (e) {
        s.analytics.track('error', { where: `custom:${this.custom.mode}:beforeStart`, message: (e as Error).message });
      }
      if (!this.scene.isActive()) return;
    }
    this.clock.start();
    this.locked = false;
    s.analytics.track('level_start', { level: this.levelNo, id: this.L.level.id, assist: this.round.assist, city: a.city });
    if (!this.custom) s.levels.prefetch(a.city, this.levelNo + 1);
    if (this.tutorial) this.showHand();
  }

  private build(): void {
    const a = app();
    const st = a.services.store.state;
    // custom rounds may hide the bar (rules.boosters 'none')
    const keys = boosterKeys(this.rules, st);
    this.lay = playLayout(this.L, { boosters: keys.length > 0 });
    const lay = this.lay;
    const u = vp.u;
    this.fx = new Fx(this);
    // Clearer items: read when the round is built (a resize rebuilds it with the same setting; changing it takes effect on the next level)
    this.looks = itemLooks(this.L, clearerItemsOn(st.settings));
    this.order = new OrderView(this, this.L, lay);
    this.board = new BoardView(this, this.L, lay, this.round, this.looks);
    // glue: the tape tears with a small pop as the pair leaves for the bag
    this.board.onRibbonSnap = (x, y) => {
      this.fx.burst(x, y, C.mustard, 8);
      this.fx.sparkle(x, y, 3);
    };
    // dispenser: an emptied machine folds away in a puff
    this.board.onMachineGone = (x, y) => {
      this.fx.burst(x, y, C.tomato, 10);
      this.fx.sparkle(x, y, 3);
    };
    // tray: the emptied tray folds away in a puff of wicker
    this.board.onTrayGone = (x, y) => this.fx.burst(x, y, C.kraftDark, 8);
    if (lay.conveyor) {
      this.conveyor = new ConveyorView(this, this.L, lay.conveyor, this.round, this.looks);
      // lockKey: belt items wear the key badge by the board's rule (held with it until the key pops)
      this.conveyor.keyOf = (r, i) => this.board.keyShown(r, i);
      this.conveyor.sync(this.round, false);
    }
    if (lay.shelf) {
      this.shelf = new ShelfView(this, this.L, lay.shelf, this.round, this.looks);
      // lockKey: shelf items of the key item wear the badge by the board's rule too
      this.shelf.keyOf = (r, i) => this.board.keyShown(r, i);
      this.shelf.sync(this.round, false);
    }
    // hiddenLine: the face-down line shows a card back (face up on a resume after the reveal)
    this.order.showFaceDown(faceDownLines(this.round));
    // lockKey: a ticket line whose item is the key wears the badge too
    this.order.setKeyLines(keyLines(this.round), false);
    // a rebuilt round (a resume, a resize): the ticket shows what core has served so far
    this.order.showServed(this.round.remaining);
    // counter strip behind the bag
    const g = this.add.graphics().setDepth(2);
    g.fillStyle(0xc98a4b, 1);
    g.fillRect(0, lay.bag.y + lay.bag.h * 0.62, this.scale.width, this.scale.height);
    g.fillStyle(C.ink, 0.25);
    g.fillRect(0, lay.bag.y + lay.bag.h * 0.62, this.scale.width, 4 * u);
    const pipH = lay.bag.h * 1.2;
    const pipW = pipH * 0.8;
    this.bag = new BagView(this, lay, this.round.cap, pipW, this.round.locked);
    this.pip = new PipView(this, lay.bag.x + pipW * 0.42, lay.bag.y + lay.bag.h * 0.98, pipH);
    // the equipped costume, else the level's city's own outfit (launch plan Phase 1 step 6, services/cityOutfit.ts)
    void this.pip.wear(outfitFor(st, this.L.level.city, (c) => hasCityOutfit(a.art, c)), a.art);
    void this.pip.look(st.skin);
    // bag contents (resume after a resize)
    for (let s = 0; s < this.round.bagLen; s++) {
      const tile = this.round.bag[s]!;
      const v = new TileView(this, tile, false, lay.tile);
      v.setItem(this.looks.of(this.L.items[this.round.item[tile]!]!));
      // collectGoal: a golden piece in the bag keeps its star corner
      v.setGolden(isGoldenPiece(this.L, tile) ? 'bag' : null);
      v.setPosition(this.bag.slotPos(s).x, this.bag.slotPos(s).y);
      this.bag.receive(tile, v, Array.from(this.round.bag.subarray(0, this.round.bagLen)));
    }
    if (lay.boosters && keys.length) this.boosters = new BoosterBar(this, lay.boosters, keys, (k) => void this.useBooster(k));
    if (this.round.L.rushMs > 0) {
      const o = lay.order;
      this.timer = lay.wide
        ? new RushTimer(this, o.x + o.w / 2, o.y + o.h + 26 * u, Math.min(o.w, 200 * u))
        : new RushTimer(this, lay.board.x + lay.board.w / 2, lay.board.y - 2 * u, Math.min(lay.board.w * 0.5, 180 * u));
    }
    this.combo = new ComboBadge(this, this.bag.center.x, this.bag.bodyTop - 34 * u);
    this.hand = new HandPointer(this);
    this.preview = new PreviewBubble(this);
    this.banner = new SlideBanner(this);
    // clearGoal: the "Closing time" tag and the items-left counter ride on the ticket (stamped on a resume after the order was done)
    const goal = clearGoalHud(this.round);
    if (goal) this.order.showClosing(goal.left, goal.orderDone);
    // collectGoal: the golden-items counter rides on the ticket the same way (stamped on a resume after the order was done)
    const gold = collectGoalHud(this.round);
    if (gold) this.order.showGolden(gold.left, gold.orderDone);
    this.tutorial = !this.custom && showHandPointer(st, this.levelNo);
    this.syncHud();
  }

  private async intro(): Promise<void> {
    const a = app();
    const st = a.services.store.state;
    const fresh = this.L.level.mechanics.filter((m) => !st.flags.seenMechanics.includes(m) && has(`mech.${m}.title`));
    // roadmap-3k §5: known specials back for the first time in this city (read before the intros mark theirs seen)
    const due = this.custom ? [] : remindersDue(st, a.city, orderOf(a.city), this.L.level.mechanics).filter((m) => has(`mech.${m}.title`));
    for (const m of fresh) {
      await a.ui.open('mechanic', { mechanic: m });
      a.services.store.update((s) => s.flags.seenMechanics.push(m));
    }
    if (!this.custom) await this.remind(fresh, due);
    a.services.audio.play('whoosh');
    await this.order.walkIn();
    this.order.pulse(this.tutorial ? 4 : 2);
    this.bag.wiggleLocks();
    this.board.wigglePadlocks();
    this.order.wiggleFaceDown();
    this.board.shineGolden();
    this.shelf?.hopEnds(this.round);
    if (this.tutorial) {
      this.hint = this.add
        .text(this.bag.center.x, this.bag.bodyTop - 30 * vp.u, t('play.tutorial.tap'), {
          fontFamily: FONT,
          fontSize: `${Math.round(22 * vp.u)}px`,
          fontStyle: '700',
          color: '#ffffff',
          stroke: '#4a2c2a',
          strokeThickness: Math.round(6 * vp.u),
        })
        .setOrigin(0.5, 1)
        .setDepth(9700);
      a.services.analytics.track('tutorial_step', { step: 'l1_start' });
    }
  }

  /**
   * Roadmap-3k §5 returning mechanic (cities from order 4): specials the player knows, back for the
   * first time in this city, get one light card before play, once per city. The full GP-07.1
   * intro wins: on a level that showed one, no reminder shows. Only what was shown counts as met
   * in this city: the intro's mechanics (just learned here), or the reminder card's. A known
   * special that came back on an intro level keeps its reminder for its next level in this city.
   */
  private async remind(fresh: readonly string[], due: readonly string[]): Promise<void> {
    const a = app();
    const s = a.services;
    const city = a.city;
    if (orderOf(city) < ERA_ORDER || (!fresh.length && !due.length)) return;
    const shown = fresh.length ? fresh : due;
    if (!fresh.length) {
      shownCards.reminder = { city, level: this.levelNo, mechanics: [...due] };
      s.analytics.track('feature_action', { feature: 'cityCards', action: 'reminder', city, mechanics: due.join(',') });
      await a.ui.open('mechanicRemind', { mechanics: [...due] });
    }
    s.store.update((st) => {
      for (const m of shown) markCard(st, remindKey(city, m));
    });
  }

  // ------------------------------------------------------------------ input

  private wireInput(): void {
    this.input.on('pointerdown', (p: Phaser.Input.Pointer, over: Phaser.GameObjects.GameObject[]) => {
      app().services.audio.unlock();
      if (over.length || this.locked) return;
      const piece = this.hitTest(p.x, p.y);
      if (piece < 0) return;
      const timer = this.time.delayedCall(LONG_PRESS_MS, () => this.showPreview());
      this.press = { piece, x: p.x, y: p.y, timer, previewed: false };
    });
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      const pr = this.press;
      if (!pr || pr.previewed) return;
      if (Math.hypot(p.x - pr.x, p.y - pr.y) > 14 * vp.u) {
        pr.timer.remove();
        this.press = null;
      }
    });
    this.input.on('pointerup', () => {
      const pr = this.press;
      this.press = null;
      if (!pr) return;
      pr.timer.remove();
      if (pr.previewed) {
        this.preview.hide();
        return;
      }
      this.tap(pr.piece);
    });
  }

  private hitTest(x: number, y: number): number {
    const b = this.board.hitTest(x, y);
    if (b >= 0) return b;
    const c = this.conveyor?.hitTest(x, y, this.round) ?? -1;
    if (c >= 0) return c;
    return this.shelf?.hitTest(x, y, this.round) ?? -1;
  }

  private showPreview(): void {
    const pr = this.press;
    if (!pr) return;
    pr.previewed = true;
    const r = this.round;
    const i = pr.piece;
    const isCrate = i >= this.L.nBoard && i < this.L.nBoard + this.L.nCrates;
    const wrapped = !isCrate && this.board.views[i]?.wrap;
    // Clearer items: the bubble names what the piece is drawn as (its substitute), so the label always matches the picture
    const itemId = isCrate || wrapped ? null : this.looks.of(this.L.items[r.item[i]!]!);
    const name = itemId ? itemName(itemId) : t(isCrate ? 'play.crate' : 'play.wrapped');
    const p = this.piecePosAny(i);
    this.preview.show(p.x, p.y, itemId, name);
    app().services.providers.haptics.impact('light');
  }

  private piecePosAny(i: number): { x: number; y: number } {
    if (isConveyorPiece(this.L, i)) return this.conveyor?.pos(i, this.round) ?? { x: 0, y: 0 };
    if (isShelfPiece(this.L, i)) return this.shelf?.pos(i, this.round) ?? { x: 0, y: 0 };
    return piecePos(this.lay, this.L, i);
  }

  private tap(piece: number): void {
    if (this.locked || this.ended || this.round.status !== Status.Playing) return;
    const a = app();
    if (!isTappable(this.round, piece)) {
      // lockKey: a padlocked tile rattles its padlock (core would answer 'locked'; the tap is never sent)
      if (tapRefusal(this.round, piece) === 'locked') {
        this.lockRefusals++;
        this.board.lockNope(piece);
      } else if (isShelfPiece(this.L, piece)) {
        // shelf: an item between the two ends wobbles where it rests
        this.shelfRefusals++;
        this.shelf?.nope(piece);
      } else this.board.nope(piece);
      a.services.audio.play('nope');
      a.services.providers.haptics.notify('warning');
      return;
    }
    const ev = this.act(Op.Tap, piece);
    if (!ev) return;
    if (this.tutorial) this.tutorialStep();
    this.animate(ev);
  }

  /** Apply one action through core (RoundSession stamps time and keeps the log). */
  private act(op: Op, arg?: number): RoundEvent[] | null {
    const res = this.session.act(op, arg);
    if (res.applied) return res.events;
    if (res.events.length) this.animate(res.events); // the rush clock ran out first
    return null;
  }

  // ------------------------------------------------------------------ events → animation

  private animate(events: RoundEvent[]): void {
    const a = app();
    if (this.custom?.onEvents && events.length) {
      try {
        this.custom.onEvents(events, this.round);
      } catch {
        /* a feature's live relay must never break the round */
      }
    }
    const audio = a.services.audio;
    // native Haptics on iOS/Android, the Vibration API on the web; both honour the vibration setting
    const haptics = a.services.providers.haptics;
    let job: PopJob | null = null;
    const later = (fn: (x: number, y: number) => void) => {
      if (job) job.after.push(fn);
      else fn(this.bag.center.x, this.bag.center.y);
    };
    // a glued partner follows the tapped tile a beat later (the pair reads as one tap)
    const delays = flightDelays(events, dur(70));
    // clearGoal: the ticket is done but the board is not (null when the same action also wins)
    const orderDone = orderDoneOf(events);
    // hiddenLine: a triple of the hidden item served it face down: its served tick flips the card first
    const flipFirst = servedFaceDown(events);
    for (const e of events) {
      switch (e.type) {
        case 'glued':
          // the ribbon snaps in board.refresh() below, once both tiles have left the board
          break;
        case 'dispensed':
          // the next item rises into the window (or the empty machine folds away) in board.refresh() below
          break;
        case 'collected': {
          const v = this.board.detach(e.tile) ?? this.conveyor?.detach(e.tile) ?? this.shelf?.detach(e.tile) ?? null;
          if (v) void this.bag.receive(e.tile, v, e.bag, delays.get(e.tile) ?? 0);
          audio.play('collect');
          this.pip.react('catch');
          haptics.impact('light');
          break;
        }
        case 'iceCracked': {
          const v = this.board.views[e.tile];
          v?.setIce(e.ice, true);
          if (v) this.fx.sparkle(v.x, v.y, 5);
          audio.play('ice');
          break;
        }
        case 'tray':
          // tray: its three items lift and pop at the tray on the `popped` below (no `collected`: the bag is untouched)
          break;
        case 'shelved':
          // shelf: the taken end flew with its `collected`; the new end lifts and lights up in shelf.sync() below
          break;
        case 'popped': {
          const pj: PopJob = { after: [] };
          job = pj;
          const step = e.comboStep;
          const color = ITEM_COLORS[e.item % ITEM_COLORS.length]!;
          const onPop = (x: number, y: number) => {
            this.fx.burst(x, y, color, 16);
            audio.play('pop', step);
            this.pip.react('pop');
            if (step >= 1) {
              this.combo.show(step);
              audio.play('combo', step);
            }
            haptics.impact('medium');
            for (const fn of pj.after) fn(x, y);
          };
          // tray: the triple pops right at the tray, never through the bag (served sparks fly from there)
          const tray = trayPopOf(this.L, e.tiles);
          if (tray >= 0) void this.board.popTray(tray, onPop);
          else void this.bag.pop(e.tiles, onPop);
          break;
        }
        case 'served': {
          const line = e.line;
          const remaining = e.remaining;
          const tick = (x: number, y: number) => {
            this.order.serve(line, remaining);
            const to = this.order.linePos(line);
            this.flySpark(x, y, to.x, to.y);
            audio.play(remaining <= 0 ? 'check' : 'serve');
          };
          // hiddenLine: a tick on the hidden line waits for its card to be face up (served face down,
          // or served again before a flip from an earlier pop has played): the card flips first
          if (line === this.L.hiddenLine) later((x, y) => this.revealLine(line, () => tick(x, y)));
          else later(tick);
          break;
        }
        case 'clutter': {
          const coins = e.coins;
          later((x, y) => this.fx.floatText(x, y - 20 * vp.u, `+${coins}`, C.coin, 26));
          break;
        }
        case 'bagUnlocked': {
          // bagLock: the bag opens now (a glued partner in this batch may already use the slot);
          // at the pop a spark runs from the finished line down to the padlock, which springs open
          const line = e.line;
          const to = this.bag.lockPos;
          this.bag.reshape(bagShape(this.bag, e)!);
          const open = () =>
            void this.bag.openLocks((x, y) => {
              this.fx.burst(x, y, C.mustard, 12);
              this.fx.sparkle(x, y, 5);
              audio.play('upgrade');
              this.pip.react('celebrate');
            });
          later(() => {
            const d = dur(360);
            if (!d || !to) return open();
            // after the served spark reaches the ticket, one runs back down to the padlock
            this.time.delayedCall(d, () => {
              const from = this.order.linePos(line);
              this.flySpark(from.x, from.y, to.x, to.y);
              this.time.delayedCall(d, open);
            });
          });
          break;
        }
        case 'unlocked': {
          // lockKey: the key's triple pops; at the pop a key flies to every padlock, which springs
          // open. The key badges stay until then (core already reads the locks as open).
          const tiles = e.tiles;
          this.board.holdKeys();
          later((x, y) => this.openPadlocks(tiles, x, y));
          break;
        }
        case 'lineRevealed': {
          // hiddenLine: after the revealer's check lands, the face-down card flips (served face down, its tick flips it)
          const line = e.line;
          if (!flipFirst) later(() => this.time.delayedCall(dur(320), () => this.revealLine(line)));
          break;
        }
        case 'golden': {
          // collectGoal: the counter keeps these until their gold sparkles land on it; at the pop
          // (after its served/clutter feedback) the sparkles fly from the pop to the counter
          const n = e.tiles.length;
          this.goldenHeld += n;
          later((x, y) => this.goldenPop(n, x, y));
          break;
        }
        case 'orderDone':
          // clearGoal/collectGoal: after the served check lands, the ticket is stamped and the counter becomes the goal
          if (orderDone) later(() => this.time.delayedCall(dur(320), () => this.onOrderDone()));
          break;
        case 'crateHit': {
          const crate = e.crate;
          const hp = e.hp;
          later(() => this.crateHit(crate, hp));
          break;
        }
        case 'undone': {
          // a glued pair comes back whole (both tiles in one event)
          for (const tile of returnedTiles(e)) {
            const v = this.bag.release(tile, e.bag);
            if (v) this.returnToBoard(tile, v);
          }
          audio.play('undo');
          break;
        }
        case 'shuffled':
          this.shuffleAnim(e.changes.map(([tile]) => tile));
          audio.play('shuffle');
          break;
        case 'magnet': {
          const pj: PopJob = { after: [] };
          job = pj;
          this.magnetAnim(e.tiles, e.line).then(({ x, y }) => {
            audio.play('pop', 0);
            this.fx.burst(x, y, ITEM_COLORS[e.item % ITEM_COLORS.length]!, 16);
            for (const fn of pj.after) fn(x, y);
          });
          audio.play('magnet');
          break;
        }
        case 'extraSlot':
          this.bag.reshape(bagShape(this.bag, e)!);
          this.pip.react('celebrate');
          audio.play('upgrade');
          break;
        case 'rescued': {
          this.bag.reshape(bagShape(this.bag, e)!);
          const bag = Array.from(this.round.bag.subarray(0, this.round.bagLen));
          for (const tile of returnedTiles(e)) {
            const v = this.bag.release(tile, bag);
            if (v) this.returnToBoard(tile, v);
          }
          break;
        }
        case 'won':
          void this.onWon(e.outcome);
          break;
        case 'failed':
          void this.onFailed(e.reason, e.rescueEligible);
          break;
        case 'uncovered':
        case 'illegal':
          break;
      }
    }
    this.board.refresh(this.round, true);
    this.conveyor?.sync(this.round, true);
    this.shelf?.sync(this.round, true);
    this.syncHud();
  }

  /** clearGoal/collectGoal: the order is served, the round goes on: stamp the ticket, point at the counter. */
  private onOrderDone(): void {
    if (this.ended || !this.scene.isActive()) return;
    const audio = app().services.audio;
    this.order.orderDone(true);
    audio.play('upgrade');
    this.pip.react('celebrate');
    const b = this.lay.board;
    const u = vp.u;
    const text = t(this.L.collect ? 'play.collectGoal.done' : 'play.clearGoal.done');
    this.banner.show(text, b.x + b.w / 2, b.y + Math.min(b.h * 0.3, 90 * u), Math.min(b.w, 360 * u));
    const at = this.order.counterPos;
    if (at) this.fx.sparkle(at.x, at.y, 6);
  }

  /**
   * collectGoal: `n` golden pieces popped at (x, y): a gold burst there and gold sparkles flying to
   * the counter, which counts them down (a bump) as the last one lands. Reduced motion: no burst
   * or flight, the counter just pulses.
   */
  private goldenPop(n: number, x: number, y: number): void {
    const land = () => {
      this.goldenHeld = Math.max(0, this.goldenHeld - n);
      this.syncGolden();
    };
    const to = this.order.counterPos;
    const d = deco(420);
    if (!d || !to) return land();
    this.fx.burst(x, y, C.star, 10);
    const f = app().art.get('vfx_sparkle');
    const count = 2 + n;
    for (let k = 0; k < count; k++) {
      // a small fan across the pop, then each one homes in on the counter
      const side = (k - (count - 1) / 2) * 22 * vp.u;
      const s = this.add.image(x + side, y - Math.abs(side) * 0.4, f.texture, f.frame).setDepth(9400).setScale(0.7 * vp.u).setTint(C.star);
      this.tweens.add({
        targets: s,
        x: to.x,
        y: to.y,
        scale: 0.3 * vp.u,
        angle: 240,
        delay: k * 50,
        duration: d,
        ease: 'Cubic.easeIn',
        onComplete: () => {
          s.destroy();
          if (k < count - 1) return;
          this.fx.sparkle(to.x, to.y, 5);
          app().services.audio.play('coin');
          land();
        },
      });
    }
  }

  /** collectGoal: the counter reads core's golden items left plus those still flying to it. */
  private syncGolden(): void {
    const gold = collectGoalHud(this.round);
    if (gold) this.order.setItemsLeft(gold.left + this.goldenHeld, true);
  }

  /**
   * hiddenLine: the face-down card flips to show its item, with a swish and a sparkle; `then` runs
   * once it is face up (at once when it already is: the flip plays once per round).
   */
  private revealLine(l: number, then?: () => void): void {
    const flipped = this.order.reveal(l, () => {
      // lockKey: a revealed line whose item is the key wears the badge now (while the locks are shut)
      this.order.setKeyLines(keyLines(this.round), true);
      then?.();
    });
    if (!flipped) return;
    app().services.audio.play('whoosh');
    const p = this.order.linePos(l);
    this.fx.sparkle(p.x, p.y, 5);
  }

  /** lockKey: keys fly from the key's pop (x, y) to each padlock, which springs open as one lands. */
  private openPadlocks(tiles: number[], x: number, y: number): void {
    const audio = app().services.audio;
    this.board.releaseKeys(this.round, true);
    this.conveyor?.sync(this.round, true);
    this.shelf?.sync(this.round, true);
    this.order.setKeyLines([], true);
    this.fx.sparkle(x, y, 6);
    let first = true;
    const onPop = (px: number, py: number) => {
      this.fx.burst(px, py, C.mustard, 10);
      this.fx.sparkle(px, py, 4);
      if (!first) return;
      first = false;
      audio.play('upgrade');
      this.pip.react('celebrate');
    };
    const step = dur(60);
    tiles.forEach((tile, k) => {
      const to = this.board.padlockPos(tile);
      if (!to) return;
      const open = () => void this.board.openPadlock(tile, onPop);
      this.flyKey(x, y, to.x, to.y, k * step, open);
    });
  }

  /** lockKey: a small key (with a sparkle trail) flies from a pop to a padlock; `arrive` runs as it lands. */
  private flyKey(x: number, y: number, tx: number, ty: number, delay: number, arrive: () => void): void {
    const d = dur(380);
    if (!d) return arrive();
    bakeLockArt(this);
    const size = this.lay.tile * 0.42;
    const k = this.add.image(x, y, KEY_BADGE).setDepth(9400);
    const s = size / k.width;
    k.setScale(s * 0.6);
    this.tweens.add({
      targets: k,
      x: tx,
      y: ty,
      scale: s,
      angle: 360,
      delay,
      duration: d,
      ease: 'Cubic.easeInOut',
      onUpdate: (tw) => {
        if (Math.floor(tw.progress * 6) !== Math.floor((tw.progress - 0.02) * 6)) this.fx.sparkle(k.x, k.y, 1);
      },
      onComplete: () => {
        k.destroy();
        arrive();
      },
    });
  }

  private returnToBoard(tile: number, v: TileView): void {
    if (isConveyorPiece(this.L, tile)) {
      this.conveyor?.attach(tile, v);
      v.destroy();
      this.conveyor?.sync(this.round, true);
      return;
    }
    // shelf: back to its own place on the row, an end again
    if (isShelfPiece(this.L, tile)) return this.shelf?.attach(tile, v, this.round, true);
    this.board.attach(tile, v, true);
  }

  private flySpark(x: number, y: number, tx: number, ty: number): void {
    const d = dur(360);
    const f = app().art.get('vfx_sparkle');
    const s = this.add.image(x, y, f.texture, f.frame).setDepth(9400).setScale(0.6 * vp.u);
    if (!d) {
      s.destroy();
      return;
    }
    this.tweens.add({
      targets: s,
      x: tx,
      y: ty,
      scale: 0.25 * vp.u,
      angle: 180,
      duration: d,
      ease: 'Cubic.easeIn',
      onComplete: () => {
        this.fx.sparkle(tx, ty, 4);
        s.destroy();
      },
    });
  }

  private crateHit(crate: number, hp: number): void {
    const v = this.board.views[crate];
    if (!v) return;
    app().services.audio.play('crate');
    if (hp > 0) {
      v.setCrateHp(hp);
      const d = dur(200);
      if (d) this.tweens.add({ targets: v, angle: { from: -6, to: 6 }, duration: d / 4, yoyo: true, repeat: 1, onComplete: () => v.setAngle(0) });
      return;
    }
    this.board.destroyPiece(crate);
    this.fx.burst(v.x, v.y, 0xc98a4b, 18);
    const d = dur(220);
    if (!d) v.destroy();
    else this.tweens.add({ targets: v, scale: 1.3, alpha: 0, duration: d, onComplete: () => v.destroy() });
  }

  private shuffleAnim(tiles: number[]): void {
    const d = dur(320);
    for (const i of tiles) {
      const v = this.board.views[i];
      if (!v || !d) continue;
      this.tweens.add({ targets: v, scaleX: 0, duration: d / 2, yoyo: true, ease: 'Sine.easeInOut', delay: (i % 5) * 20 });
    }
    // item textures swap at the half-way point
    this.time.delayedCall(d / 2, () => this.board.refresh(this.round, false));
  }

  private magnetAnim(tiles: number[], line: number): Promise<{ x: number; y: number }> {
    const to = this.order.linePos(line);
    const d = dur(420);
    const views = tiles.map((i) => this.board.detach(i) ?? this.conveyor?.detach(i) ?? this.shelf?.detach(i) ?? null).filter((v): v is TileView => !!v);
    return new Promise((resolve) => {
      if (!d || !views.length) {
        views.forEach((v) => v.destroy());
        resolve(to);
        return;
      }
      views.forEach((v, k) => {
        v.setDepth(7000);
        this.tweens.add({
          targets: v,
          x: to.x,
          y: to.y,
          scale: 0.4,
          duration: d,
          delay: k * 60,
          ease: 'Back.easeIn',
          onComplete: () => {
            v.destroy();
            if (k === views.length - 1) resolve(to);
          },
        });
      });
    });
  }

  /** Booster enablement, Pip's worried state (bag 5–6 of 7) and tutorial pointer. */
  private syncHud(): void {
    const r = this.round;
    const playing = r.status === Status.Playing && !this.ended;
    if (this.boosters) {
      const inv = app().services.store.state.inventory;
      this.boosters.sync(inv, {
        undo: playing && canUndo(r),
        shuffle: playing && canShuffle(r),
        magnet: playing && canMagnet(r),
        extraSlot: playing && canExtraSlot(r),
      });
    }
    if (playing) this.pip.setBase(r.bagLen >= r.cap - 2 && r.bagLen > 0 ? 'worried' : 'idle');
    // clearGoal: the counter follows every change (a tap, an undo, a rescue, a magnet, a dispenser)
    const goal = clearGoalHud(r);
    if (goal) this.order.setItemsLeft(goal.left, true);
    // collectGoal: so does the golden-items one (a pop's sparkles count down as they land)
    this.syncGolden();
  }

  override update(time: number): void {
    if (!this.ready) return;
    this.bag.update(time);
    const r = this.round;
    if (this.timer && r.deadline > 0) {
      const left = r.deadline - this.clock.now();
      this.timer.set(left, r.L.rushMs);
      const sec = Math.ceil(left / 1000);
      if (sec <= 10 && sec > 0 && sec !== this.lastTick && r.status === Status.Playing && !this.locked) {
        this.lastTick = sec;
        app().services.audio.play('timerTick', sec % 2);
      }
    }
    if (!this.ended && !this.locked && r.status === Status.Playing && r.deadline > 0 && this.clock.now() >= r.deadline) {
      const ev = this.act(Op.Tick);
      if (ev) this.animate(ev);
    }
  }

  // ------------------------------------------------------------------ tutorial (Flow A)

  private tutorialStep(): void {
    this.tutorialTaps++;
    if (this.tutorialTaps === 1) app().services.analytics.track('tutorial_step', { step: 'l1_first_tap' });
    if (this.tutorialTaps === 1 && this.hint) this.hint.setText(t('play.tutorial.match'));
    if (this.tutorialTaps >= 3) {
      this.hand.hide();
      this.tutorial = false;
      if (this.hint) this.tweens.add({ targets: this.hint, alpha: 0, delay: 1200, duration: 400 });
      return;
    }
    this.showHand();
  }

  /** Flow A: the hand points at an uncovered ORDER tile (the hint bot's pick when it is one). */
  private showHand(): void {
    const r = this.round;
    const needed = new Set<number>();
    // hiddenLine: a face-down line's item is not known, so the hand never points at it
    for (let l = 0; l < r.L.nLines; l++) if (r.remaining[l]! > 0 && lineShown(r, l)) needed.add(r.L.lineItem[l]!);
    let best = recommendTap(r);
    // per tile (tappableTiles lists a glued pair once, by its lower tile)
    if (best >= 0 && !needed.has(r.item[best]!)) best = tappablePieces(r).find((i) => needed.has(r.item[i]!)) ?? best;
    if (best < 0) return this.hand.hide();
    const p = this.piecePosAny(best);
    this.hand.show(p.x, p.y, this.lay.tile);
  }

  // ------------------------------------------------------------------ boosters

  private async useBooster(k: BoosterKey): Promise<void> {
    if (this.locked || this.ended || !this.boosters) return;
    const a = app();
    const s = a.services;
    if (!this.boosters.isEnabled(k)) {
      s.audio.play('nope');
      // lockKey: a magnet held back only by the padlocks points at them
      if (k === 'magnet' && magnetHeldByLocks(this.round)) this.board.rattlePadlocks();
      // hiddenLine: one held back only by the face-down line points at its card
      if (k === 'magnet' && magnetHeldByHiddenLine(this.round)) this.order.rattleFaceDown();
      return;
    }
    // the server's replay budget (never send a log it will refuse) and custom-round rules
    const block = boosterBlock(this.rules, this.rec.boosters, k);
    if (block) {
      s.audio.play('nope');
      if (block === 'roundCap') a.ui.toast(t('booster.roundCap', { name: t(`booster.${k}`) }));
      if (block === 'onePerLevel') a.ui.toast(t('play.boosterLimit'));
      return;
    }
    if (s.store.state.inventory[k] <= 0) {
      const price = economy.boosterPrices[k];
      // 2L: a rewarded ad refills an empty booster, but not one spent earlier in this round (the server still counts it
      // until the round is submitted, so it would refuse the ad); COPPA accounts are not offered it
      const ad = !s.store.state.coppa && !(this.rec.boosters[k] ?? 0) && s.ads.likely('booster');
      const choice = await a.ui.open('buyBooster', { booster: k, price, gems: s.store.state.gems, ad, adQty: economy.ads.rewarded.booster.qty });
      if (!choice) return;
      if (choice === 'ad') {
        if (!(await this.boosterAd(k))) return;
      } else {
        let bought = false;
        s.store.update((st) => (bought = buyBooster(st, k)));
        if (!bought) return;
        s.sync.enqueue(op.buyBooster(k));
        s.audio.play('coin');
      }
    }
    const res = this.session.act(BOOSTER_OP[k]);
    if (!res.applied) {
      if (res.events.length) this.animate(res.events); // the rush clock ran out first
      else if (res.illegal === 'shuffle_unsolvable') a.ui.toast(t('booster.shuffle.none'));
      else s.audio.play('nope');
      this.syncHud();
      return;
    }
    s.store.update((st) => {
      // the server debits it when the round is submitted: until then a server snapshot must not give it back (sync.ts reconcile)
      if (consumeBooster(st, k)) (st.roundBoosters ??= {})[k] = (st.roundBoosters[k] ?? 0) + 1;
    });
    this.rec.boosters[k] = (this.rec.boosters[k] ?? 0) + 1;
    s.analytics.track('booster_used', { booster: k, level: this.levelNo });
    this.animate(res.events);
  }

  /** A rewarded ad for one booster (2L): the round clock waits while it plays. */
  private async boosterAd(k: BoosterKey): Promise<boolean> {
    const a = app();
    const s = a.services;
    this.clock.hold('ad');
    try {
      const r = await s.ads.watch('booster', k);
      if (!r.ok) {
        const key = adFailureToast(r.reason);
        if (key) a.ui.toast(t(key));
        return false;
      }
      // the answer's snapshot is adopted unless ops are queued; then the grant is predicted until the next sync
      s.store.update((st) => (st.inventory[k] = Math.max(st.inventory[k], r.granted.boosters?.[k] ?? 1)));
      return true;
    } finally {
      this.clock.release('ad');
    }
  }

  /** The round is over (or abandoned): what it spent is the server's to debit now, so reconcile stops holding it back. */
  private endRoundBoosters(): void {
    app().services.store.update((st) => (st.roundBoosters = {}));
  }

  // ------------------------------------------------------------------ pause

  private async pause(): Promise<void> {
    if (!this.ready || this.ended) return;
    const a = app();
    const choice = await a.ui.open('pause', { level: this.levelNo, canMap: hasMark(a.services.store.state, MARK.mapRevealed) });
    if (choice === 'resume') return;
    this.ended = true;
    this.endRoundBoosters();
    a.services.analytics.track('level_end', { level: this.levelNo, result: 'quit', taps: this.round.taps });
    if (choice === 'restart') go(this, 'play', { level: this.levelNo, ...(this.custom ? { custom: this.custom } : {}) });
    else go(this, 'map');
  }

  // ------------------------------------------------------------------ win (SCR-05)

  private async onWon(o: RoundOutcome): Promise<void> {
    const a = app();
    const s = a.services;
    this.ended = true;
    this.endRoundBoosters();
    this.locked = true;
    this.clock.hold('end');
    this.hand.hide();
    // tray: a winning tray tap pops at the tray, not in the bag; its pop plays out first too
    await atMost(Promise.all([this.bag.idle(), this.board.idle()]), 450);
    this.pip.hold('win');
    this.order.allHappy();
    s.audio.play('win');
    s.providers.haptics.notify('success');
    const { width: W } = this.scale;
    this.fx.confetti(W / 2, this.lay.board.y + this.lay.board.h * 0.3, 70);
    this.sweep();
    s.analytics.track('level_end', {
      level: this.levelNo,
      id: this.L.level.id,
      mode: this.rec.mode,
      result: 'win',
      stars: o.stars,
      taps: o.taps,
      par: this.L.par,
      durationMs: o.durationMs,
      coins: o.coins,
      boosters: o.boosters,
      rescues: o.rescues,
      bestCombo: o.bestCombo,
    });
    if (this.custom) return this.endCustom(this.custom);
    const { summary, body } = finishRound(s.store, s.sync, this.round, this.rec, s.now());
    if (this.levelNo <= 3) s.analytics.track('tutorial_step', { step: `l${this.levelNo}_win` });
    if (summary.streak.changed) s.analytics.track('streak', { count: summary.streak.state.count, milestone: summary.streak.milestone });
    await wait(380);
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      const nonce = this.rec.adNonces.doubleTips;
      s.sync.release(body.roundId, (op) => {
        if (nonce && op.kind === 'round') op.body.adNonces = { ...op.body.adNonces, doubleTips: nonce };
      });
    };
    const beatOn = o.stars === 3 && a.remote.flags.beatLinks !== false && a.remote.flags.share !== false && s.api.enabled;
    // roadmap-3k shop finale (from city order 4): the win screen points at the shop's next upgrade, once per shop
    const shape = cityShape(a.city);
    const finaleShop = shape ? shopFinale(shape, this.levelNo) : null;
    const finale = finaleShop && !cardSeen(s.store.state, `finale:${finaleShop}`) ? finaleShop : null;
    if (finale) shownCards.finaleBanner = { shop: finale, level: this.levelNo };
    const choice = await a.ui.open('win', {
      level: this.levelNo,
      stars: o.stars,
      newBest: summary.newBest,
      reward: o.baseCoins,
      tips: o.tips,
      sweep: o.sweepCoins,
      xp: summary.xp,
      bestCombo: o.bestCombo,
      doubleTips: s.ads.likely('doubleTips') ? 'ad' : 'tryLater',
      onDoubleTips: async () => {
        if (released) return false;
        const r = await s.ads.watch('doubleTips');
        if (!r.ok) {
          const key = adFailureToast(r.reason);
          if (key) a.ui.toast(t(key));
          return false;
        }
        this.rec.adNonces.doubleTips = r.nonce;
        s.store.update((st) => applyDoubleTips(st, o.tips));
        return true;
      },
      canBeat: beatOn,
      onBeat: async () => {
        // SO-03: the server links only an accepted 3-star round, so send it now
        release();
        await openView(a, 'beatShare', { levelId: this.L.level.id, roundId: body.roundId, level: this.levelNo, stars: o.stars, taps: o.taps });
      },
      finale: finale ? shopTitle(s.store.state, finale, shape?.shops.find((x) => x.id === finale)?.name ?? finale) : null,
    });
    if (finale) {
      s.store.update((st) => markCard(st, `finale:${finale}`));
      s.analytics.track('feature_action', { feature: 'cityCards', action: choice === 'shop' ? 'finale_shop' : 'finale_seen', shop: finale });
    }
    release();
    a.ui.setHud({ coinPulse: a.ui.hud.coinPulse + 1 });
    const after = shape && isGrandOpening(shape, this.levelNo) ? await this.cityOpening(o.baseCoins) : null;
    await this.afterRound(summary.streak.milestone, summary.streakGrant, summary.levelFrom, summary.levelTo, summary.unlocks);
    await s.ads.afterRound(this.levelNo, 'win');
    // a city moment's route goes elsewhere first: the level-up's "Try it" waits for the next map visit
    const holdFeature = () => {
      if (this.pendingFeature) a.session.featureAfter = this.pendingFeature;
    };
    if (after?.to === 'play') {
      holdFeature();
      a.city = after.city;
      go(this, 'play', { level: after.level });
      return;
    }
    if (after) {
      holdFeature();
      go(this, 'map', after.focus ? { focus: after.focus } : { open: after.open });
      return;
    }
    if (finale && choice === 'shop') {
      holdFeature();
      // a shop not open yet (an earlier one still under renovation): My shop, as the map's button opens it
      const st = s.store.state;
      go(this, 'shop', { shopId: isShopUnlocked(st, finale) ? finale : activeShop(st, a.city).id });
      return;
    }
    const route = routeAfterWin(s.store.state);
    if (route.to === 'play') go(this, 'play', { level: route.level });
    else go(this, 'map', { reveal: route.reveal, ...(this.pendingFeature ? { open: this.pendingFeature } : {}) });
  }

  /**
   * Roadmap-3k Grand Opening (a city's last level, from order 4), once per city: the city's
   * celebration, then the next city's card ("Visit" glides the map up to it), or after the last
   * city the end-of-content card. Where the player goes next; null for the usual route.
   */
  private async cityOpening(coins: number): Promise<CityNext | null> {
    const a = app();
    const s = a.services;
    const city = a.city;
    if (cardSeen(s.store.state, `open:${city}`)) return null;
    const next = nextCity(s.store.state, city);
    // the next city's teaser street wears the lock as the journey draws it: only while that city cannot be played yet
    const lock = !!next && journeyNow(a).find((st) => st.city === next)?.kind !== 'play';
    shownCards.cityOpening = { city, next, coins, ...(next ? { lock } : {}) };
    s.analytics.track('grand_opening', { city, level: this.levelNo });
    await openView(a, 'cityOpening', { city, name: cityName(city), coins });
    s.store.update((st) => markCard(st, `open:${city}`));
    if (!next) return this.endOfContent();
    const pick = await openView<'visit' | 'later'>(a, 'nextCity', { city: next, name: cityTitle(next), lock });
    s.analytics.track('feature_action', { feature: 'cityCards', action: pick === 'visit' ? 'next_city_visit' : 'next_city_later', city: next });
    return pick === 'visit' ? { to: 'map', focus: next } : null;
  }

  /** After the last city's Grand Opening: "You've visited every city!" and what is left to play. */
  private async endOfContent(): Promise<CityNext | null> {
    const a = app();
    const s = a.services;
    s.store.update((st) => markCard(st, 'end'));
    const act = await openView<'replay' | 'daily' | 'events' | 'close'>(a, 'endOfContent', { from: 'grandOpening' });
    if (act === 'replay') {
      const r = replayTarget(s.store.state);
      return r ? { to: 'play', city: r.city, level: r.level } : null;
    }
    if (act === 'daily') return { to: 'map', open: 'dailyPuzzle' };
    if (act === 'events') return { to: 'map', open: 'events' };
    return null;
  }

  /** MT-06.2 streak milestone and MT-05 level-up / OVL-03 feature intros. */
  private async afterRound(milestone: number | null, grant: Grant | null, from: number, to: number, unlocks: string[]): Promise<void> {
    const a = app();
    const s = a.services;
    if (milestone !== null && grant && streakMilestoneDue(s.store.state, milestone)) {
      s.store.update((st) => st.prompts.streakSeen.push(milestone));
      await openView(a, 'streakMilestone', { days: milestone, grant });
    }
    if (to > from) {
      s.audio.play('levelup');
      const choice = await a.ui.open('levelUp', { level: to, unlocks });
      for (const f of unlocks) s.analytics.track('feature_unlock', { feature: f, level: to });
      s.store.update((st) => st.flags.seenUnlocks.push(...unlocks));
      if (choice !== 'ok') this.pendingFeature = choice;
    }
  }

  /** a feature the level-up screen's "Try it" asked for, opened on the map */
  private pendingFeature: string | null = null;

  /**
   * A feature-launched round ends on the feature's terms: its `submit` answers what the
   * result shows (standard screen with its lines, or the feature's own view), never the
   * campaign flow (no level-up, streak or interstitial).
   */
  private async endCustom(c: CustomRound): Promise<void> {
    const a = app();
    const s = a.services;
    const o = this.round.outcome!;
    const body = buildSubmission(this.round, this.rec, o, s.now());
    await wait(o.result === 'win' ? 600 : 300);
    let res: CustomResult = {};
    try {
      res = await c.submit({ rec: this.rec, body, outcome: o, round: this.round });
    } catch (e) {
      s.analytics.track('error', { where: `custom:${c.mode}:submit`, message: (e as Error).message });
      a.ui.toast(t('state.error.title'));
    }
    if (!this.scene.isActive()) return;
    a.ui.setHud({ coinPulse: a.ui.hud.coinPulse + 1 });
    let again = false;
    if (c.result === 'custom' && res.view) await openView(a, res.view.kind, res.view.props ?? {});
    else {
      again =
        (await a.ui.open('roundResult', {
          result: o.result,
          stars: o.stars,
          reason: o.result === 'win' ? null : this.failReason,
          title: res.title ?? null,
          lines: res.lines ?? [],
          coins: res.coins ?? 0,
          retry: !!res.retry,
        })) === 'retry';
    }
    if (again) go(this, 'play', { level: this.levelNo, custom: c });
    else go(this, 'map', res.next ? { open: res.next.open, openProps: res.next.props ?? {} } : {});
  }

  /** GP-03.2 sweep: leftover tiles fly off as bonus coins. */
  private sweep(): void {
    // shelf: the items left on its row fly off with the board's
    const tiles = [...this.board.remaining(), ...(this.shelf?.remaining() ?? [])];
    this.board.clearRibbons();
    this.board.clearMachines();
    this.board.clearTrays();
    const d = dur(420);
    const target = { x: this.scale.width / 2, y: this.lay.order.y };
    tiles.forEach((v, k) => {
      if (!d) return;
      v.setDepth(8000 + k);
      this.tweens.add({
        targets: v,
        x: target.x,
        y: target.y,
        scale: 0.2,
        alpha: 0.2,
        duration: d,
        delay: Math.min(300, k * 12),
        ease: 'Cubic.easeIn',
        onComplete: () => v.setVisible(false),
      });
    });
  }

  // ------------------------------------------------------------------ fail + rescue (SCR-06, Flow C)

  private async onFailed(reason: 'bagFull' | 'timeout' | 'stuck', eligible: boolean): Promise<void> {
    const a = app();
    const s = a.services;
    this.failReason = reason;
    this.locked = true;
    this.clock.hold('end');
    this.hand.hide();
    await atMost(Promise.all([this.bag.idle(), this.board.idle()]), 450);
    this.pip.hold('panic');
    this.bag.shake();
    this.order.allSad();
    this.fx.shake(0.008, 260);
    s.audio.play('fail');
    s.providers.haptics.notify('error');
    await wait(750);
    if (eligible && this.rules.rescue) {
      const kind = reason === 'timeout' ? 'time' : 'slot';
      s.analytics.track('rescue_offered', { level: this.levelNo, kind, free: rescueOffer(s.store.state, kind).free, sets: rescueSets(this.round) });
      // MO-01.2: a failed or missing ad never ends the offer; the player can pick again
      for (let attempt = 0; attempt < 4; attempt++) {
        const offer = rescueOffer(s.store.state, kind);
        // collectGoal: the sheet tells ticket sets and golden items apart
        const left = rescueLeft(this.round);
        const choice = await a.ui.open('rescue', {
          kind,
          reason,
          setsLeft: left.sets,
          ...(left.golden !== null ? { goldenLeft: left.golden } : {}),
          free: offer.free,
          gems: offer.gems,
          canAffordGems: offer.canAffordGems,
          boosters: offer.boosters,
          ad: s.ads.likely('rescue') ? 'ad' : 'tryLater',
          adOnly: this.rules.rescue === 'ad',
        });
        if (choice === 'decline') break;
        if (!(await this.payForRescue(choice))) continue;
        const ev = this.act(kind === 'time' ? Op.RescueTime : Op.RescueSlot);
        if (!ev) break;
        this.rec.rescues.push(choice);
        s.analytics.track('rescue_taken', { level: this.levelNo, method: choice, kind });
        if (choice === 'free') s.analytics.track('tutorial_step', { step: 'first_rescue' });
        s.audio.play('rescue');
        this.pip.release();
        this.order.allNeutral();
        this.locked = false;
        this.clock.release('end');
        this.animate(ev);
        return;
      }
    }
    this.ended = true;
    this.endRoundBoosters();
    s.analytics.track('level_end', { level: this.levelNo, id: this.L.level.id, mode: this.rec.mode, result: 'fail', reason, taps: this.round.taps, rescues: this.round.rescues });
    if (this.custom) return this.endCustom(this.custom);
    finishRound(s.store, s.sync, this.round, this.rec, s.now());
    await s.ads.afterRound(this.levelNo, 'fail');
    const choice = await a.ui.open('fail', { level: this.levelNo, reason, canMap: hasMark(s.store.state, MARK.mapRevealed) });
    if (choice === 'retry') go(this, 'play', { level: this.levelNo });
    else go(this, 'map');
  }

  private async payForRescue(method: RescueMethod): Promise<boolean> {
    const a = app();
    const s = a.services;
    if (method === 'ad') {
      const r = await s.ads.watch('rescue');
      if (!r.ok) {
        const key = adFailureToast(r.reason);
        if (key) a.ui.toast(t(key));
        return false;
      }
      this.rec.adNonces.rescue.push(r.nonce);
    }
    let ok = false;
    s.store.update((st) => (ok = payRescue(st, method)));
    return ok;
  }

  // ------------------------------------------------------------------ lifecycle

  private wireLifecycle(): void {
    const a = app();
    const onHidden = () => this.clock.hold('hidden');
    const onVisible = () => this.clock.release('hidden');
    this.game.events.on(Phaser.Core.Events.HIDDEN, onHidden);
    this.game.events.on(Phaser.Core.Events.VISIBLE, onVisible);
    a.ui.onModalChange = (open) => (open ? this.clock.hold('modal') : this.clock.release('modal'));
    this.events.once('shutdown', () => {
      this.game.events.off(Phaser.Core.Events.HIDDEN, onHidden);
      this.game.events.off(Phaser.Core.Events.VISIBLE, onVisible);
      a.ui.onModalChange = null;
      this.tweens.killAll();
    });
    onResizeSettled(this, () => {
      if (this.ended || this.locked || a.ui.top) return;
      this.scene.restart({ level: this.levelNo, resume: { session: this.session, clock: this.clock }, ...(this.custom ? { custom: this.custom } : {}) });
    });
  }

  // ------------------------------------------------------------------ test hooks

  /** CSS-px centres of every tappable piece (with its item index). */
  tappablePositions(): { tile: number; item: number; x: number; y: number }[] {
    if (!this.ready || this.locked || this.round.status !== Status.Playing) return [];
    const out: { tile: number; item: number; x: number; y: number }[] = [];
    for (let i = 0; i < this.L.n; i++) {
      if (!isTappable(this.round, i)) continue;
      const p = this.piecePosAny(i);
      out.push({ tile: i, item: this.round.item[i]!, x: p.x / vp.dpr, y: p.y / vp.dpr });
    }
    return out;
  }

  /** Bag contents as item indices, ticket sets still to serve, the goal's sets (rescue), cap, ticket items still needed (test hooks). */
  roundInfo(): { bag: number[]; sets: number; goalSets: number; cap: number; needed: number[] } {
    const r = this.round;
    const needed: number[] = [];
    for (let l = 0; l < r.L.nLines; l++) if (r.remaining[l]! > 0) needed.push(r.L.lineItem[l]!);
    return { bag: Array.from(r.bag.subarray(0, r.bagLen), (t) => r.item[t]!), sets: setsRemaining(r), goalSets: rescueSets(r), cap: r.cap, needed };
  }

  /** Glue ribbons on the board: shown, and dimmed because a tile of the pair is covered (test hooks). */
  glueInfo(): { pairs: number; shown: number; covered: number } {
    return { pairs: this.L.nGlue, ...this.board.ribbonInfo() };
  }

  /** bagLock: slots still locked in core, and padlocks the bag still draws (test hooks). */
  bagLockInfo(): { locked: number; shown: number } {
    return { locked: this.round.locked, shown: this.bag.locksShown };
  }

  /**
   * lockKey: the locks (shut in core, locked tiles), padlocks and key badges drawn (board, belt,
   * ticket), the key item, and taps refused on a padlock (test hooks).
   */
  lockKeyInfo(): { shut: boolean; locked: number[]; padlocks: number; keys: number; ticketKeys: number; keyItem: number; refused: number } | null {
    const L = this.L;
    if (!L.lockedTiles.length) return null;
    const b = this.board.lockInfo();
    return {
      shut: this.round.lockShut,
      locked: L.lockedTiles.slice(),
      padlocks: b.padlocks,
      keys: b.keys + (this.conveyor?.keysShown() ?? 0) + (this.shelf?.keysShown() ?? 0),
      ticketKeys: this.order.keysShown,
      keyItem: L.lockKey,
      refused: this.lockRefusals,
    };
  }

  /**
   * clearGoal: items still on the board and the ticket done (core), and what the HUD draws: the
   * tag, the counter's text, the done stamps, the counter's emphasis, banners shown (test hooks).
   */
  clearGoalInfo(): { left: number; orderDone: boolean; tag: boolean; counter: string; stamps: number; emphasis: boolean; banners: number } | null {
    const goal = clearGoalHud(this.round);
    const drawn = this.order.closingInfo();
    if (!goal || !drawn) return null;
    return { ...goal, ...drawn, banners: this.banner.shown };
  }

  /**
   * collectGoal: golden pieces not yet popped and the ticket done (core), and what the HUD draws:
   * gold frames on the board, the counter's text, banners shown (test hooks).
   */
  collectGoalInfo(): { left: number; orderDone: boolean; framed: number; counter: string; banners: number } | null {
    const gold = collectGoalHud(this.round);
    const drawn = this.order.closingInfo();
    if (!gold || !drawn) return null;
    return { ...gold, framed: this.board.goldFrames, counter: drawn.counter, banners: this.banner.shown };
  }

  /**
   * hiddenLine: the line still face down in core, its index, whether the ticket draws it face down,
   * and the card flips played (test hooks).
   */
  hiddenLineInfo(): { hidden: boolean; line: number; faceDown: boolean; reveals: number } | null {
    const l = this.L.hiddenLine;
    if (l < 0) return null;
    return { hidden: this.round.lineHidden, line: l, faceDown: this.order.isFaceDown(l), reveals: this.order.reveals };
  }

  /** tray: trays on the level, trays the board draws, and tray pops played (test hooks); null without trays. */
  trayInfo(): { trays: number; drawn: number; popped: number } | null {
    if (!this.L.nTrays) return null;
    return { trays: this.L.nTrays, ...this.board.trayInfo() };
  }

  /**
   * Clearer items: the level's lookalikes, whether the setting draws them as their substitutes, for
   * every piece on the board and in the bag its engine item id and the art it is drawn with (the
   * atlas frame's id; `real` when it is an atlas frame, not a stand-in), and what the long-press
   * bubble shows while it is up. Null on a level without lookalikes (test hooks; the engine's own
   * ids and indices are the other hooks').
   */
  lookInfo(): { on: boolean; lookalikes: Record<string, string>; board: LookRow[]; bag: LookRow[]; preview: { art: string; real: boolean; name: string } | null } | null {
    const lookalikes = this.L.level.lookalikes;
    if (!lookalikes) return null;
    const row = (v: TileView): LookRow => {
      const drawn = v.drawnArt();
      return { piece: v.piece, item: this.L.items[this.round.item[v.piece]!]!, art: drawn?.id ?? '', real: drawn?.real ?? false };
    };
    return {
      on: this.looks.active,
      lookalikes: { ...lookalikes },
      board: this.board.views.flatMap((v) => (v && !v.isCrate ? [row(v)] : [])),
      bag: this.bag.views().map(row),
      preview: this.preview.info(),
    };
  }

  /**
   * shelf: core's items left on the (first) shelf and its two ends (pieces), the items the view
   * draws on its row, and taps refused on an inner item (test hooks); null without a shelf.
   */
  shelfInfo(): { left: number; ends: [number, number]; drawn: number; refused: number } | null {
    if (!this.L.nShelves) return null;
    const st = shelfState(this.round, 0);
    return { left: st.left, ends: st.ends, drawn: this.shelf?.shown ?? 0, refused: this.shelfRefusals };
  }

  /** threeCustomers: customer cards (ticket papers) the order view draws (test hooks). */
  get customerCards(): number {
    return this.order.cards;
  }

  /** threeCustomers: CSS-px boxes of the ticket lines' item icons and count badges, with their card (test hooks). */
  ticketLines(): { line: number; customer: number; done: boolean; x: number; y: number; w: number; h: number; badge: { x: number; y: number; w: number; h: number } }[] {
    const k = vp.dpr;
    const css = (b: { x: number; y: number; w: number; h: number }) => ({ x: b.x / k, y: b.y / k, w: b.w / k, h: b.h / k });
    return this.order.lineBoxes().map((b) => ({ ...b, ...css(b), badge: css(b.badge) }));
  }

  /** Booster buttons: shown greyed out (core can't apply them now) or not (test hooks). */
  boosterInfo(): Partial<Record<BoosterKey, boolean>> {
    return this.boosters?.enabledMap() ?? {};
  }

  /** CSS-px centre of a board piece or a shelf item, tappable or not (test hooks: e.g. a padlocked tile, an item between the shelf's ends). */
  piecePosition(i: number): { x: number; y: number } | null {
    if (!this.ready || !(this.board.drawn(i) || this.shelf?.drawn(i))) return null;
    const p = this.piecePosAny(i);
    return { x: p.x / vp.dpr, y: p.y / vp.dpr };
  }

  /** Dispenser machines: items left, the badge text, shown, dimmed, and the shown item (test hooks). */
  dispenserInfo(): { left: number; counter: string; shown: boolean; covered: boolean; item: number }[] {
    return this.board.machineInfo(this.round);
  }

  /** Pip in the round (test hooks): the level's city, the costume he wears in it and the one whose overlay is drawn. */
  outfit(): { city: string; wearing: string; drawn: string | null } {
    return { city: this.L.level.city, wearing: this.pip.wearing, drawn: this.pip.dressed };
  }

  /** CSS-px positions of canvas buttons (boosters). */
  targets(): Record<string, { x: number; y: number }> {
    const out: Record<string, { x: number; y: number }> = {};
    for (const b of this.boosters?.positions() ?? []) out[`booster_${b.key}`] = { x: b.x / vp.dpr, y: b.y / vp.dpr };
    return out;
  }

  bestTapPosition(): { tile: number; x: number; y: number } | null {
    if (!this.ready || this.locked || this.round.status !== Status.Playing) return null;
    const best = recommendTap(this.round);
    if (best < 0) return null;
    const p = this.piecePosAny(best);
    return { tile: best, x: p.x / vp.dpr, y: p.y / vp.dpr };
  }

  /** Taps are accepted right now (intro done, no modal, round playing). */
  get interactive(): boolean {
    return this.ready && !this.locked && !this.ended && this.round.status === Status.Playing;
  }

  get statusName(): string {
    if (!this.ready) return 'loading';
    return ['playing', 'won', 'failed'][this.round.status] ?? 'unknown';
  }
}
